/**
 * Taxonomy chuẩn "Cấp học → Khối lớp → Môn học" theo Chương trình GDPT 2018.
 *
 * Đây là NGUỒN SỰ THẬT DUY NHẤT phía frontend cho danh mục môn học/khối lớp.
 * Backend có bản đồng bộ tại Modules/EduApp/Config/education_taxonomy.php —
 * khi sửa danh mục ở đây phải sửa cả file đó.
 *
 * Đặc thù từng cấp (theo tổ chức môn dạy trong nhà trường VN):
 * - Tiểu học: GV chủ nhiệm dạy hầu hết môn cơ bản; GV đặc thù (specialist)
 *   chỉ dạy 1 môn (Tiếng Anh, Tin học, Âm nhạc, Mỹ thuật, GDTC).
 *   Một số môn chỉ có ở dải lớp nhất định (TNXH lớp 1-3, Khoa học lớp 4-5...).
 * - THCS: có môn tích hợp (KHTN = Lý+Hóa+Sinh, Lịch sử & Địa lý) — trường
 *   `components` liệt kê phân môn để UI cho chọn và bơm vào prompt AI.
 * - THPT: GV chuyên sâu 1 môn, tổ chức theo bộ môn độc lập.
 */
import { GradeLevel } from '../types';

export interface SubjectInfo {
  /** Tên chuẩn hiển thị — đồng thời là giá trị lưu DB (cột `subject`) */
  name: string;
  icon: string;
  /** Chỉ có ở các khối này trong cấp (bỏ trống = mọi khối của cấp) */
  grades?: number[];
  /** Môn tích hợp: danh sách phân môn (KHTN → Vật lý/Hóa học/Sinh học) */
  components?: string[];
  /** Tiểu học: môn do GV đặc thù dạy (không thuộc nhóm GV chủ nhiệm) */
  specialist?: boolean;
}

/** Giá trị sentinel cho lựa chọn "Khác" — người dùng tự nhập tên môn */
export const OTHER_SUBJECT = 'Khác';

const TIEU_HOC_SUBJECTS: SubjectInfo[] = [
  { name: 'Tiếng Việt', icon: '📖' },
  { name: 'Toán', icon: '📐' },
  { name: 'Đạo đức', icon: '💛' },
  { name: 'Tự nhiên và Xã hội', icon: '🌱', grades: [1, 2, 3] },
  { name: 'Khoa học', icon: '🔬', grades: [4, 5] },
  { name: 'Lịch sử và Địa lý', icon: '🗺️', grades: [4, 5], components: ['Lịch sử', 'Địa lý'] },
  { name: 'Tiếng Anh', icon: '🇬🇧', specialist: true },
  { name: 'Tin học và Công nghệ', icon: '💻', grades: [3, 4, 5], specialist: true },
  { name: 'Âm nhạc', icon: '🎵', specialist: true },
  { name: 'Mỹ thuật', icon: '🎨', specialist: true },
  { name: 'Giáo dục thể chất', icon: '⚽', specialist: true },
  { name: 'Hoạt động trải nghiệm', icon: '🌟' },
];

const THCS_SUBJECTS: SubjectInfo[] = [
  { name: 'Toán', icon: '📐' },
  { name: 'Ngữ văn', icon: '📖' },
  { name: 'Tiếng Anh', icon: '🇬🇧' },
  { name: 'Khoa học tự nhiên', icon: '🔬', components: ['Vật lý', 'Hóa học', 'Sinh học'] },
  { name: 'Lịch sử và Địa lý', icon: '🗺️', components: ['Lịch sử', 'Địa lý'] },
  { name: 'Giáo dục công dân', icon: '⚖️' },
  { name: 'Công nghệ', icon: '🔧' },
  { name: 'Tin học', icon: '💻' },
  { name: 'Giáo dục thể chất', icon: '⚽' },
  { name: 'Âm nhạc', icon: '🎵' },
  { name: 'Mỹ thuật', icon: '🎨' },
  { name: 'Hoạt động trải nghiệm, hướng nghiệp', icon: '🌟' },
];

const THPT_SUBJECTS: SubjectInfo[] = [
  { name: 'Toán', icon: '📐' },
  { name: 'Ngữ văn', icon: '📖' },
  { name: 'Tiếng Anh', icon: '🇬🇧' },
  { name: 'Lịch sử', icon: '🏛️' },
  { name: 'Vật lý', icon: '⚡' },
  { name: 'Hóa học', icon: '🧪' },
  { name: 'Sinh học', icon: '🌿' },
  { name: 'Địa lý', icon: '🌍' },
  { name: 'Giáo dục kinh tế và pháp luật', icon: '⚖️' },
  { name: 'Tin học', icon: '💻' },
  { name: 'Công nghệ', icon: '🔧' },
  { name: 'Âm nhạc', icon: '🎵' },
  { name: 'Mỹ thuật', icon: '🎨' },
  { name: 'Giáo dục thể chất', icon: '⚽' },
  { name: 'Giáo dục quốc phòng và an ninh', icon: '🛡️' },
  { name: 'Hoạt động trải nghiệm, hướng nghiệp', icon: '🌟' },
];

/** Mầm non tổ chức theo lĩnh vực phát triển thay vì môn học */
const MAM_NON_SUBJECTS: SubjectInfo[] = [
  { name: 'Phát triển thể chất', icon: '🤸' },
  { name: 'Phát triển nhận thức', icon: '🧠' },
  { name: 'Phát triển ngôn ngữ', icon: '💬' },
  { name: 'Phát triển tình cảm và kỹ năng xã hội', icon: '🤝' },
  { name: 'Phát triển thẩm mỹ', icon: '🎨' },
];

/** GDTX cấp THPT: chương trình văn hóa 7 môn bắt buộc/lựa chọn */
const GDTX_SUBJECTS: SubjectInfo[] = [
  { name: 'Toán', icon: '📐' },
  { name: 'Ngữ văn', icon: '📖' },
  { name: 'Lịch sử', icon: '🏛️' },
  { name: 'Vật lý', icon: '⚡' },
  { name: 'Hóa học', icon: '🧪' },
  { name: 'Sinh học', icon: '🌿' },
  { name: 'Địa lý', icon: '🌍' },
  { name: 'Giáo dục kinh tế và pháp luật', icon: '⚖️' },
  { name: 'Tin học', icon: '💻' },
  { name: 'Tiếng Anh', icon: '🇬🇧' },
];

export interface LevelTaxonomy {
  /** Các khối lớp thuộc cấp (rỗng = không áp dụng khối số, nhập tự do) */
  grades: number[];
  subjects: SubjectInfo[];
}

export const EDUCATION_TAXONOMY: Record<GradeLevel, LevelTaxonomy> = {
  [GradeLevel.MAM_NON]: { grades: [], subjects: MAM_NON_SUBJECTS },
  [GradeLevel.TIEU_HOC]: { grades: [1, 2, 3, 4, 5], subjects: TIEU_HOC_SUBJECTS },
  [GradeLevel.THCS]: { grades: [6, 7, 8, 9], subjects: THCS_SUBJECTS },
  [GradeLevel.THPT]: { grades: [10, 11, 12], subjects: THPT_SUBJECTS },
  [GradeLevel.GDTX]: { grades: [10, 11, 12], subjects: GDTX_SUBJECTS },
  [GradeLevel.DAI_HOC]: { grades: [], subjects: [] },
};

/** Danh sách khối của một cấp học (rỗng nếu cấp không dùng khối số) */
export function getGradesForLevel(level: GradeLevel): number[] {
  return EDUCATION_TAXONOMY[level]?.grades ?? [];
}

/**
 * Danh sách môn của một cấp học, tùy chọn lọc theo khối
 * (vd Tiểu học lớp 2 sẽ không có Khoa học/Lịch sử và Địa lý).
 */
export function getSubjectsForLevel(level: GradeLevel, grade?: number): SubjectInfo[] {
  const subjects = EDUCATION_TAXONOMY[level]?.subjects ?? [];
  if (grade == null) return subjects;
  return subjects.filter(s => !s.grades || s.grades.includes(grade));
}

/** Tra thông tin môn theo tên chuẩn trong một cấp (trả undefined nếu là môn tự nhập) */
export function getSubjectInfo(level: GradeLevel, name: string): SubjectInfo | undefined {
  return EDUCATION_TAXONOMY[level]?.subjects.find(s => s.name === name);
}

/** Môn tích hợp (có phân môn) — dùng để hiện ô chọn phân môn trong form */
export function isIntegratedSubject(level: GradeLevel, name: string): boolean {
  return !!getSubjectInfo(level, name)?.components?.length;
}

/**
 * Nhóm môn Tiểu học cho GV chủ nhiệm (dạy hầu hết môn cơ bản của lớp mình)
 * — loại các môn do GV đặc thù đảm nhiệm.
 */
export function getHomeroomSubjects(grade?: number): SubjectInfo[] {
  return getSubjectsForLevel(GradeLevel.TIEU_HOC, grade).filter(s => !s.specialist);
}

/** Suy ra cấp học từ khối số (1-5 → Tiểu học, 6-9 → THCS, 10-12 → THPT) */
export function inferLevelFromGrade(grade: number): GradeLevel | undefined {
  if (grade >= 1 && grade <= 5) return GradeLevel.TIEU_HOC;
  if (grade >= 6 && grade <= 9) return GradeLevel.THCS;
  if (grade >= 10 && grade <= 12) return GradeLevel.THPT;
  return undefined;
}
